<script lang="ts" setup>
import { PropertyType, PropertyTypeDTO } from '@/core/collections/contracts-api/companies/property-types/property-type.interface';
import {
   getDefaultPropertyTypeData,
   usePropertyTypeSchema,
} from '@/core/collections/contracts-api/companies/property-types/property-type.schema';
import { Customer } from '@/core/collections/contracts-api/customers/customer.interface';
import GenericList from '@/core/components/templates/generic-list/GenericList.vue';
import store from '@/core/store';
import { NxtGenericFormModal } from '@nxtpeople/nxt-styles';
import CompanyPropertyTypeForm from './CompanyPropertyTypeForm.vue';

//#region Globals
const { t } = useI18n();
const { dispatch } = useStore();
const { PROPERTY_TYPE_SCHEMA } = usePropertyTypeSchema();

const companyId = inject('companyId', ref<number>());
const customer = inject('customer', ref<Customer>());

//#endregion

//#region Generic List
const { overview, loading, loadingCreate, loadingDelete, loadingChange, getData, createData, changeData, deleteData, deleteMultipleData } =
   useGenericList<PropertyType, PropertyTypeDTO>({
      translation: (length = 1) => t('companies.property-types.property-type', length),
      actions: {
         getAll: (query) => {
            return dispatch('company/propertyType/getPropertyTypes', { companyId: companyId.value, query });
         },
         create: (propertyType) => {
            return dispatch('company/propertyType/createPropertyType', { companyId: companyId.value, propertyType });
         },
         change: (propertyType) => {
            return dispatch('company/propertyType/changePropertyType', {
               companyId: companyId.value,
               propertyTypeId: propertyType.id,
               propertyType,
            });
         },
         delete: (propertyTypeId) => {
            return dispatch('company/propertyType/deletePropertyType', { companyId: companyId.value, propertyTypeId });
         },
      },
   });

watchEffect(() => {
   if (!store.hasModule(['company', 'propertyType'])) return;
   if (companyId.value) getData({ per_page: '25' });
});

function createPropertyType(propertyType: PropertyTypeDTO, closeCreateItemModal: () => void) {
   createData(propertyType, () => {
      closeCreateItemModal();
   });
}

function changePropertyType(propertyType: PropertyTypeDTO, closeChangeItemModal: () => void) {
   changeData(propertyType, () => {
      closeChangeItemModal();
   });
}
//#endregion
</script>

<template>
   <GenericList
      :name="'company-property-types'"
      :translation="(length) => t('companies.property-types.property-type', length)"
      :loading="loading"
      :mode="'light'"
      :list-items="overview?.data"
      :meta="overview?.meta"
      :class="'company-property-types'"
      :headings="[{ label: $t('generic.name'), style: { width: '100%' } }, { style: { minWidth: '3rem' } }]"
      :list-item-padding="'var(--nxt-gutter-small)'"
   >
      <template #listItem="{ item }">
         <td>{{ item.name }}</td>
         <td>
            <NxtIconButton
               :name="'trash'"
               :disabled="loadingDelete"
               :hover-text="$t('actions.delete-type', { type: $t('companies.property-types.property-type') })"
               @on-click="deleteData({ itemToDelete: item })"
            />
         </td>
      </template>

      <template #actionBarActions="{ selectedItems, deselectAllItems }">
         <NxtIconButton
            v-if="$auth('update', customer?.scopes)"
            :name="'trash'"
            :disabled="loading"
            :hover-text="$t('actions.delete-type', { type: $t('companies.property-types.property-type') })"
            @on-click="deleteMultipleData({ itemsToDelete: selectedItems, done: deselectAllItems })"
         />
      </template>

      <template v-if="$auth('update', customer?.scopes)" #createModal="{ closeCreateItemModal }">
         <NxtGenericFormModal
            :data="getDefaultPropertyTypeData()"
            :modal-type="'create'"
            :width="'50rem'"
            :data-type="t('companies.property-types.property-type')"
            :loading="loadingCreate"
            :schema="PROPERTY_TYPE_SCHEMA"
            @close="closeCreateItemModal"
            @save="(data) => createPropertyType(data, closeCreateItemModal)"
         >
            <template #default="params">
               <CompanyPropertyTypeForm v-bind="params" />
            </template>
         </NxtGenericFormModal>
      </template>

      <template #changeModal="{ closeChangeItemModal, item }">
         <NxtGenericFormModal
            :data="{ ...item, company_properties: item.properties }"
            :modal-type="'change'"
            :width="'50rem'"
            :data-type="t('companies.property-types.property-type')"
            :loading="loadingChange"
            :schema="PROPERTY_TYPE_SCHEMA"
            @close="closeChangeItemModal"
            @save="(data) => changePropertyType(data, closeChangeItemModal)"
         >
            <template #default="params">
               <CompanyPropertyTypeForm v-bind="params" />
            </template>
         </NxtGenericFormModal>
      </template>
   </GenericList>
</template>

<style lang="scss" scoped>
.company-property-types {
}
</style>
